// src/components/PopupPictureUpload.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  Card,
  CardMedia,
  CardActions,
} from "@mui/material";
import {
  CloudUpload,
  Delete,
} from "@mui/icons-material";

const PopupPictureUpload = () => {
  const [imageData, setImageData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const apiUrl = process.env.REACT_APP_API_URL;

  // Fetch existing image on mount
  useEffect(() => {
    fetchExistingImage();
  }, []);

  const fetchExistingImage = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/popupImage`);
      if (response.ok) {
        const data = await response.json();
        if (data.imageData) {
          setImageData(data.imageData);
        }
      }
    } catch (error) {
      console.error("Error fetching existing image:", error);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setSnackbar({ open: true, message: "Please select a valid image file", severity: "error" });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setSnackbar({ open: true, message: "Image size must be less than 5MB", severity: "error" });
      return;
    }

    setLoading(true);
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      // Update local state (for immediate preview)
      setImageData(result);
      // Send the image to the server
      fetch(`${apiUrl}/api/popupImage`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ imageData: result }),
      })
        .then((res) => res.json())
        .then((data) => {
          console.log("Image uploaded successfully:", data);
          setImageData(data.imageData);
          setSnackbar({ open: true, message: "Image uploaded successfully!", severity: "success" });
        })
        .catch((error) => {
          console.error("Error uploading image to server:", error);
          setSnackbar({ open: true, message: "Failed to upload image", severity: "error" });
          setImageData(null);
        })
        .finally(() => {
          setLoading(false);
        });
    };
    reader.readAsDataURL(file);
  };

  const handleDeleteImage = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${apiUrl}/api/popupImage`, {
        method: "DELETE",
      });

      const data = await response.json();

      if (response.ok) {
        console.log("Image deleted successfully:", data);
        setImageData(null);
        setSnackbar({ open: true, message: "Image deleted successfully!", severity: "success" });
      } else {
        console.error("Delete failed:", data);
        setSnackbar({ open: true, message: data.error || "Failed to delete image", severity: "error" });
      }
    } catch (error) {
      console.error("Error deleting image from server:", error);
      setSnackbar({ open: true, message: "Network error: Failed to delete image", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Upload Section */}
      {!imageData && !loading && (
        <Box
          component="label"
          sx={{
            p: 8,
            cursor: 'pointer',
            transition: 'all 0.3s',
            textAlign: 'center',
            display: 'block',
            '&:hover': {
              transform: 'scale(1.02)',
            },
          }}
        >
          <input
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            style={{ display: 'none' }}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CloudUpload sx={{ fontSize: 80, color: 'primary.main' }} />
            <Box>
              <Typography variant="h5" gutterBottom fontWeight={600}>
                Upload Popup Image
              </Typography>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                Click to browse or drag and drop your image here
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                Max file size: 5MB • Supported formats: JPG, PNG, GIF
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ p: 10, textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary" sx={{ mt: 3 }}>
            Processing image...
          </Typography>
        </Box>
      )}

      {/* Image Preview */}
      {imageData && !loading && (
        <Card sx={{ maxWidth: 800, mx: 'auto' }}>
          <Box sx={{ p: 3, backgroundColor: 'action.hover' }}>
            <CardMedia
              component="img"
              image={imageData}
              alt="Popup preview"
              sx={{
                maxHeight: 500,
                width: '100%',
                objectFit: 'contain',
                borderRadius: 2,
              }}
            />
          </Box>
          <CardActions sx={{ justifyContent: 'space-between', p: 3, backgroundColor: 'background.paper' }}>
            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Current Popup Image
              </Typography>
              <Typography variant="caption" color="text.secondary">
                This image will be displayed as a popup on your website
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                component="label"
                variant="contained"
                size="medium"
                startIcon={<CloudUpload />}
              >
                Replace Image
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="medium"
                startIcon={<Delete />}
                onClick={handleDeleteImage}
              >
                Delete
              </Button>
            </Box>
          </CardActions>
        </Card>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PopupPictureUpload;
